import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { delegate } from 'utils-decorators';
import { environment } from '../../environments/environment';
import { Application } from '../application/application';
import { ParceiroProvider } from '../application/parceiro';
import { Token } from '../data/token.data';
import { UsuarioLogadoData } from '../data/usuario/usuario-logado.data';
import { UsuarioData } from '../data/usuario/usuario.data';
import { USE_TOKEN } from '../interceptors/auth.interceptor';
import { InternalCacheStorage } from '../utils/cache.utils';
import { InternalStorage } from '../utils/storage/storage.util';
import { ToastService } from './utils/toast.service';
import { OutsideZone } from './utils/zone/zone.decorators';

export enum AutenticacaoStorageKeys {
	ACCESS_TOKEN = 'ACCESS_TOKEN',
	USUARIO = 'USUARIO'
}

@Injectable({
	providedIn: 'root'
})
@OutsideZone()
export class AutenticacaoService {

	#context: HttpContext = new HttpContext().set(USE_TOKEN, false);

	#storage: InternalStorage = new InternalStorage({
		name: Application.STORAGE_NAME,
		storeName: Application.USER_STORAGE_NAME
	});

	#onUsuarioChange: ReplaySubject<UsuarioLogadoData | undefined> = new ReplaySubject();

	constructor(
    private route: ActivatedRoute,
		private http: HttpClient,
		private toast: ToastService,
		private ngZone: NgZone
	) {
		this.#storage
			.get(AutenticacaoStorageKeys.USUARIO)
			.then(usuario => {
				if (usuario)
					this.ngZone.run(() => this.#onUsuarioChange.next(usuario));
			});
	}

	@delegate()
	async autenticarUsuario(
		paramUsuario: string,
		paramSenha: string,
		siglaParceiro: string
	) {
		const url = `${environment.urlBaseAPI}autenticar`;
		const sigla = siglaParceiro ?? '';

		// TODO: Alterar backend para receber a sigla da URL
		const dadosUsuario = await firstValueFrom(
			this.http.post<Token>(url,
				{
					codigoParceiro: sigla,
					usuario: paramUsuario,
					senha: paramSenha
				},
				{
					context: this.#context
				})
		);

		const token = dadosUsuario.token?.replace('Bearer ', '');
		delete dadosUsuario.token;
		await this.#storage.set(AutenticacaoStorageKeys.ACCESS_TOKEN, token);

		sessionStorage.setItem('idParceiro',dadosUsuario.idParceiro.toString())

		const usuario = new UsuarioLogadoData({
			...dadosUsuario,
			nomeParceiro: dadosUsuario.nomeParceiro ?? 'Parceiro',
			urlParceiro: sigla,
      descHierarquia: dadosUsuario.descHierarquia ?? 'Hierarquia',
		});
		await this.#storage.set(AutenticacaoStorageKeys.USUARIO, usuario);

		this.ngZone.run(() => this.#onUsuarioChange.next(usuario));
		return dadosUsuario;
	}

	@delegate()
	async autoLogin(
		paramId: string,
		siglaParceiro?: string
	) {
		const url = `${environment.urlBaseAPI}auto-login`;
		const sigla = siglaParceiro ?? '';

		const dadosUsuario = await firstValueFrom(
			this.http.post<Token>(url,
				{ id: paramId },
				{ context: this.#context }
			)
		);

		const token = dadosUsuario.token?.replace('Bearer ', '');
		delete dadosUsuario.token;
		await this.#storage.set(AutenticacaoStorageKeys.ACCESS_TOKEN, token);

		const usuario = new UsuarioLogadoData({
			...dadosUsuario,
			nomeParceiro: dadosUsuario.nomeParceiro ?? 'Parceiro',
			urlParceiro: sigla,
      descHierarquia: dadosUsuario.descHierarquia ?? 'Hierarquia',
		});
		await this.#storage.set(AutenticacaoStorageKeys.USUARIO, usuario);

		this.#onUsuarioChange.next(usuario);

		this.ngZone.run(() => this.#onUsuarioChange.next(usuario));
		return dadosUsuario;
	}

	@delegate()
	public async getUsuario(): Promise<UsuarioLogadoData | undefined> {
		return this.#storage.get(AutenticacaoStorageKeys.USUARIO);
	}

	@delegate()
	public async getToken(): Promise<string> {
		return this.#storage.get(AutenticacaoStorageKeys.ACCESS_TOKEN);
	}

	// @delegate()
	// public async refreshToken() {
	// 	const refreshToken = await this.#storage.get(AutenticacaoStorageKeys.ACCESS_TOKEN);

	// 	if (refreshToken)
	// 		await this.#storage.set(AutenticacaoStorageKeys.ACCESS_TOKEN, refreshToken.accessToken);

	// 	return firstValueFrom(
	// 		this.http
	// 			.post<Token>(`http://localhost:3007/v1/login`,
	// 				{
	// 					refreshToken
	// 				})
	// 	);
	// }

	@delegate()
	public async expirarToken() {
		this.toast.danger({
			header: 'Sessão Encerrada!',
			message: 'Token Inválido.',
			width: '280px'
		});
		await this.signout();
	}

	@delegate()
	public async isLoggedIn() {
		const idToken = await this.#storage.get(AutenticacaoStorageKeys.ACCESS_TOKEN);
		return !!idToken;
	}

	@delegate()
	public async signout() {
		await this.#storage.clear();
		await InternalCacheStorage.clearCache();

		this.ngZone.run(() => this.#onUsuarioChange.next(undefined));
	}

	@delegate()
	public async recuperarSenha(login: string, parceiro?: any) {
		// const sistema = ParceiroProvider.getSistema(parceiro);
		// if (!sistema) throw new Error('Sistema não definido');

		const url = `${environment.urlBaseAPI}usuario/esqueceu/senha`;
		const response = await firstValueFrom(
			this.http.post<{ email: string }>(url, {
// // // // // // //				idParceiro: sistema.id,
				codigoParceiro: parceiro,
				descLogin: login
			})
		);

		if (!response.email)
			throw new Error('Resposta da requisição inválida!');

		return response;
	}

	@delegate()
	public async redefinirSenha(token: string, novaSenha: string) {
		const isPrimeiroAcesso = await this.verificarPrimeiroAcesso(token);

		const url = `${environment.urlBaseAPI}` + (isPrimeiroAcesso ? `usuario/primeiro/acesso` : `usuario/redefinir/senha`);
		const response = await firstValueFrom(
			this.http.post<any>(url, {
				token,
				novaSenha
			})
		);

		if (!response.retVal2?.ret && typeof response?.id !== 'number')
			throw new Error(response?.message ? response?.message : 'Resposta da requisição inválida!');

		return response;
	}

	@delegate()
	public async verificarPrimeiroAcesso(token: string) {
		const url = `${environment.urlBaseAPI}usuario/validar/primeiro/acesso`;

		const response = await firstValueFrom(
			this.http.post<Partial<UsuarioData>>(url, { token }, { observe: 'response' })
		).catch(err => err instanceof HttpErrorResponse ? err.error : null)

		return (response?.status !== 400 && typeof response?.body?.id === 'number');
	}

	public onUserChange(): Observable<UsuarioLogadoData | undefined> {
		return this.#onUsuarioChange.asObservable();
	}

}
